import lowerCase from 'lodash/lowerCase';

function getTrendsAppIdFromUserSettings (rootGetters) {
    const trendsDataSettings = rootGetters['userSettings/settingByName']('trends-data-settings', true)?.value;

    if (trendsDataSettings?.appId) {
        return trendsDataSettings.appId;
    }

    if (trendsDataSettings?.app?.id) {
        return trendsDataSettings.app.id;
    }

    return undefined;
}

function getAppIdFromRetentionHistory (rootGetters, reportIndex = 0) {
    const retentionHistory = rootGetters['savedReports/retentionHistory'] || [];

    if (retentionHistory.length > 0) {
        const mostRecentReportId = retentionHistory[reportIndex]?.id;
        const mostRecentReport = rootGetters['savedReports/reportById']?.(mostRecentReportId);

        if (mostRecentReport?.appId === 'all') {
            reportIndex++;

            return getAppIdFromRetentionHistory(rootGetters, reportIndex);
        }

        if (mostRecentReport?.appId) {
            const { appId } = mostRecentReport;
            if (typeof appId === 'number' || typeof appId === 'string') {
                return appId;
            }
        }
    }

    const firstAvailableApp = rootGetters['apps/firstAvailableApp'];

    return firstAvailableApp?.id;
}

/**
 * Resolves the trends app id for flows that can await (e.g. insight fetches).
 * Loads saved reports when user settings do not pin an app, so retention history / reportById match getters.
 */
export async function getAppId (rootGetters, dispatch, reportIndex = 0) {
    const fromSettings = getTrendsAppIdFromUserSettings(rootGetters);
    if (fromSettings !== undefined && fromSettings !== null) {
        return fromSettings;
    }

    await dispatch('savedReports/loadAll', {}, { root: true });

    return getAppIdFromRetentionHistory(rootGetters, reportIndex);
}

function getItemByType (itemType, itemId, rootGetters) {
    if (itemType === 'Page') {
        return rootGetters['pages/pageById'](itemId);
    }
    if (itemType === 'Feature') {
        return rootGetters['features/featureById'](itemId);
    }
    if (itemType === 'TrackType') {
        return rootGetters['trackTypes/trackTypeById'](itemId);
    }

    return null;
}

export default {
    namespaced: true,
    getters: {
        getApp: (state, getters, rootState, rootGetters) => {
            const appId =
                getTrendsAppIdFromUserSettings(rootGetters) ?? getAppIdFromRetentionHistory(rootGetters);

            return {
                id: appId,
                name: rootGetters['apps/appNameFromId'](appId),
                icon: rootGetters['apps/getIconByAppId'](appId)?.type,
            };
        },
        getAppDisplayName (state, getters, rootState, rootGetters) {
            const app = getters.getApp;
            if (!app?.id) return '';

            const fullApp = rootGetters['apps/getAppById'](app.id);

            return fullApp?.displayName || fullApp?.name || app.name || '';
        },
        getEvents: (state, getters, rootState, rootGetters) => {
            const trendsDataSettings = rootGetters['userSettings/settingByName']('trends-data-settings', true)?.value;

            if (trendsDataSettings) {
                const { events } = trendsDataSettings;

                const selectedAppId =
                    getTrendsAppIdFromUserSettings(rootGetters) ?? getAppIdFromRetentionHistory(rootGetters);

                if (events) {
                    // Normalize events to array (handle both array and object formats)
                    const eventsArray = Array.isArray(events) ? events : Object.values(events);

                    return eventsArray
                        .map((event) => {
                            if (!event) {
                                return null;
                            }

                            const { kind, id } = event;

                            if (!kind || !id) {
                                return null;
                            }

                            // Always verify the item still exists in the system
                            const itemDetails = getItemByType(kind, id, rootGetters);
                            if (!itemDetails || itemDetails.appId !== selectedAppId) {
                                return null;
                            }

                            return {
                                id,
                                icon: kind === 'TrackType' ? 'zap' : lowerCase(kind),
                                name: itemDetails.name,
                                kind,
                                appId: itemDetails.appId,
                            };
                        })
                        .filter((event) => !!event);
                }
            }

            return [];
        },
    },
};
